// 初始化画布
export const initCanvas = (canvas: HTMLCanvasElement, config) => {
  const dpr = window.devicePixelRatio || 1
  const ctx = canvas.getContext('2d')
  const { containerW: w, containerH: h } = config

  canvas.width = w * dpr
  canvas.height = h * dpr

  if (!ctx) return

  ctx.scale(dpr, dpr)
}

// 清除画布
export const clearDraw = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d')

  if (!ctx) return

  ctx.clearRect(0, 0, canvas.width, canvas.height)
}

export type BarConfig = {
  // x轴值
  x: number
  // y轴值
  h: number
  // 柱宽度
  barWidth: number
  // 填充颜色
  color: string
  // 圆角
  radius: number
  // 容器高度
  barHeight: number
}

// 绘制柱子
export const drawBar = (canvas: HTMLCanvasElement, config: BarConfig) => {
  const { x, h, barWidth, radius, color, barHeight } = config
  const y = barHeight - h
  const ctx = canvas.getContext('2d')

  if (!ctx) return

  ctx.beginPath()
  ctx.moveTo(x + radius, y + h)
  ctx.arcTo(x, y + h, x, y + h - radius, radius)
  ctx.lineTo(x, y + radius)
  ctx.arcTo(x, y, x + radius, y, radius)
  ctx.lineTo(x + barWidth - radius, y)
  ctx.arcTo(x + barWidth, y, x + barWidth, y + radius, radius)
  ctx.lineTo(x + barWidth, y + h - radius)
  ctx.arcTo(x + barWidth, y + h, x + barWidth - radius, y + h, radius)
  ctx.closePath()
  ctx.fillStyle = color
  ctx.fill()
}

export type FillConfig = {
  // 点坐标值
  points: { x: number; y: number }[]
  bottomRaduis: number
  // 填充颜色
  fillColor: string
  // 容器高度
  containerH: number
  // 控制点变化程度
  tension?: number
}

// 绘制波浪填充区域
export const drawFillPath = (canvas: HTMLCanvasElement, config: FillConfig) => {
  const ctx = canvas.getContext('2d')
  const { points, fillColor, containerH, bottomRaduis } = config

  if (!ctx) return

  ctx.beginPath()
  ctx.moveTo(points[0].x, points[0].y)
  if (points.length === 1) {
    ctx.lineTo(points[1].x, points[0].y)
  } else {
    // 顶部贝塞尔曲线（与线条路径一致）
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i === 0 ? i : i - 1]
      const p1 = points[i]
      const p2 = points[i + 1]
      const p3 = points[i + 2] || p2
      const cp1x = p1.x + (p2.x - p0.x) / 6
      const cp1y = p1.y + (p2.y - p0.y) / 6
      const cp2x = p2.x - (p3.x - p1.x) / 6
      const cp2y = p2.y - (p3.y - p1.y) / 6
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y)
    }
  }
  ctx.lineTo(points[points.length - 1].x, containerH - bottomRaduis) // 右侧到底部
  ctx.arcTo(points[points.length - 1].x, containerH, points[points.length - 1].x - bottomRaduis, containerH, bottomRaduis)
  ctx.lineTo(points[0].x + bottomRaduis, containerH) // 底部向左
  ctx.arcTo(points[0].x, containerH, points[0].x, containerH - bottomRaduis, bottomRaduis)

  ctx.closePath() // 左侧回起点
  ctx.fillStyle = fillColor
  ctx.fill()
}

export type LineConfig = {
  // 点坐标值
  points: { x: number; y: number }[]
  // 线颜色
  lineColor: string
  // 线宽
  lineWidth: number
  // 控制点变化程度
  tension?: number
}

// 绘制波浪线条
export const drawLinePath = (canvas: HTMLCanvasElement, config: LineConfig) => {
  const ctx = canvas.getContext('2d')
  const { points, lineColor, lineWidth } = config

  if (!ctx) return

  ctx.beginPath()

  ctx.moveTo(points[0].x, points[0].y)
  if (points.length === 1) {
    ctx.lineTo(points[1].x, points[0].y)
  } else {
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i === 0 ? i : i - 1]
      const p1 = points[i]
      const p2 = points[i + 1]
      const p3 = points[i + 2] || p2
      const cp1x = p1.x + (p2.x - p0.x) / 6
      const cp1y = p1.y + (p2.y - p0.y) / 6
      const cp2x = p2.x - (p3.x - p1.x) / 6
      const cp2y = p2.y - (p3.y - p1.y) / 6
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y)
    }
  }

  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineWidth
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke()
}

export type RingConfig = {
  // 容器中心点位置
  centerX: number
  centerY: number
  // 最外层圈半径
  wideRadius: number
  // 填充颜色
  color: string
  // 环宽
  lineWidth: number
  // 首尾是否圆角
  isRound: boolean
  // 开始角度
  startAngle?: number
  // 结束角度
  endAngle?: number
}

// 绘制环状
export const drawRing = (canvas: HTMLCanvasElement, config: RingConfig) => {
  const ctx = canvas.getContext('2d')
  const { centerX, centerY, wideRadius, color, lineWidth, isRound, startAngle, endAngle } = config

  if (!ctx) return

  ctx.beginPath()
  ctx.arc(centerX, centerY, wideRadius, startAngle ?? -Math.PI / 2, endAngle ?? Math.PI * 2, false)
  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth
  ctx.lineCap = isRound ? 'round' : 'butt'
  ctx.stroke()
}

// 基础配置
export type BaseConfig = {
  // 容器宽
  containerW: number
  // 容器高
  containerH: number
}

// DrawCanvas类型
export type DrawCanvasType = {
  canvas: HTMLCanvasElement
  config: BaseConfig
  initCanvas: () => void
  clearDraw: () => void
  drawBar: (barConfig: BarConfig) => void
  drawFillPath: (fillConfig: FillConfig) => void
  drawLinePath: (lineConfig: LineConfig) => void
  drawRing: (ringConfig: RingConfig) => void
}

export class DrawCanvas {
  canvas: HTMLCanvasElement
  config: BaseConfig

  constructor(options) {
    this.canvas = options.canvas
    this.config = options.config
  }

  public initCanvas() {
    if (!this.canvas) return

    const dpr = window.devicePixelRatio || 1
    const ctx = this.canvas.getContext('2d')
    const { containerW: w, containerH: h } = this.config

    this.canvas.width = w * dpr
    this.canvas.height = h * dpr

    if (!ctx) return

    ctx.scale(dpr, dpr)
  }

  public clearDraw() {
    if (!this.canvas) return

    const ctx = this.canvas.getContext('2d')

    if (!ctx) return

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
  }

  public drawBar(barConfig: BarConfig) {
    if (!this.canvas) return

    const { x, h, barWidth, radius, color, barHeight } = barConfig
    const y = barHeight - h
    const ctx = this.canvas.getContext('2d')

    if (!ctx) return

    ctx.beginPath()
    ctx.moveTo(x + radius, y + h)
    ctx.arcTo(x, y + h, x, y + h - radius, radius)
    ctx.lineTo(x, y + radius)
    ctx.arcTo(x, y, x + radius, y, radius)
    ctx.lineTo(x + barWidth - radius, y)
    ctx.arcTo(x + barWidth, y, x + barWidth, y + radius, radius)
    ctx.lineTo(x + barWidth, y + h - radius)
    ctx.arcTo(x + barWidth, y + h, x + barWidth - radius, y + h, radius)
    ctx.closePath()
    ctx.fillStyle = color
    ctx.fill()
  }

  public drawFillPath(fillConfig: FillConfig) {
    if (!this.canvas) return

    const ctx = this.canvas.getContext('2d')
    const { points, fillColor, containerH, bottomRaduis, tension = 1 } = fillConfig

    if (!ctx) return

    ctx.beginPath()
    ctx.moveTo(points[0].x, points[0].y)
    if (points.length === 1) {
      ctx.lineTo(points[1].x, points[0].y)
    } else {
      // 顶部贝塞尔曲线（与线条路径一致）
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i === 0 ? i : i - 1]
        const p1 = points[i]
        const p2 = points[i + 1]
        const p3 = points[i + 2] || p2
        const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6
        const cp1y = p1.y + ((p2.y - p0.y) * tension) / 6
        const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6
        const cp2y = p2.y - ((p3.y - p1.y) * tension) / 6
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y)
      }
    }
    ctx.lineTo(points[points.length - 1].x, containerH - bottomRaduis) // 右侧到底部
    ctx.arcTo(points[points.length - 1].x, containerH, points[points.length - 1].x - bottomRaduis, containerH, bottomRaduis)
    ctx.lineTo(points[0].x + bottomRaduis, containerH) // 底部向左
    ctx.arcTo(points[0].x, containerH, points[0].x, containerH - bottomRaduis, bottomRaduis)

    ctx.closePath() // 左侧回起点
    ctx.fillStyle = fillColor
    ctx.fill()
  }

  public drawLinePath(lineConfig: LineConfig) {
    if (!this.canvas) return

    const ctx = this.canvas.getContext('2d')
    const { points, lineColor, lineWidth, tension = 1 } = lineConfig

    if (!ctx) return

    ctx.beginPath()

    ctx.moveTo(points[0].x, points[0].y)
    if (points.length === 1) {
      ctx.lineTo(points[1].x, points[0].y)
    } else {
      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i === 0 ? i : i - 1]
        const p1 = points[i]
        const p2 = points[i + 1]
        const p3 = points[i + 2] || p2
        const cp1x = p1.x + ((p2.x - p0.x) * tension) / 6
        const cp1y = p1.y + ((p2.y - p0.y) * tension) / 6
        const cp2x = p2.x - ((p3.x - p1.x) * tension) / 6
        const cp2y = p2.y - ((p3.y - p1.y) * tension) / 6
        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y)
      }
    }

    ctx.strokeStyle = lineColor
    ctx.lineWidth = lineWidth
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
  }

  public drawRing(ringConfig: RingConfig) {
    if (!this.canvas) return

    const ctx = this.canvas.getContext('2d')
    const { centerX, centerY, wideRadius, color, lineWidth, isRound, startAngle, endAngle } = ringConfig

    if (!ctx) return

    ctx.beginPath()
    ctx.arc(centerX, centerY, wideRadius, startAngle ?? -Math.PI / 2, endAngle ?? Math.PI * 2, false)
    ctx.strokeStyle = color
    ctx.lineWidth = lineWidth
    ctx.lineCap = isRound ? 'round' : 'butt'
    ctx.stroke()
  }
}
